import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileText, Upload, Trash2, Eye, AlertTriangle, CheckCircle2, ShieldAlert, Bell, Mail, Loader2, FileDown,
} from "lucide-react";
import { api, formatDate, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";

const STATUS_BADGES = {
  ok: { label: "Conforme", cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  incomplete: { label: "Incomplet", cls: "bg-red-100 text-red-700", Icon: ShieldAlert },
  expired: { label: "Expiré", cls: "bg-red-100 text-red-700", Icon: AlertTriangle },
  expiring_soon: { label: "Expire bientôt", cls: "bg-amber-100 text-amber-700", Icon: AlertTriangle },
};

/**
 * Conformité documentaire d'un sous-traitant — Phase 175.
 *
 * Props:
 *   - sub        : objet sous-traitant
 *   - onUpdated  : callback appelé après mise à jour (refresh parent)
 */
export default function SubcontractorComplianceTab({ sub, onUpdated }) {
  const [docTypes, setDocTypes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [required, setRequired] = useState([]);
  const [alertsEnabled, setAlertsEnabled] = useState(sub.compliance_alerts_enabled ?? true);
  const [emailEnabled, setEmailEnabled] = useState(!!sub.compliance_auto_email_enabled);
  const [savingToggles, setSavingToggles] = useState(false);
  const [uploading, setUploading] = useState(null); // doc_type_id during upload
  const [validityDraft, setValidityDraft] = useState({}); // {doc_type_id: 'YYYY-MM-DD'}
  const [viewerDoc, setViewerDoc] = useState(null); // {file_name, mime_type, file_base64}
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    try {
      const [t, d] = await Promise.all([
        api.getSubcontractorDocTypes(),
        api.getSubcontractorDocuments(sub.id),
      ]);
      setDocTypes(t.data || []);
      setDocs(d.data || []);
      setRequired(sub.required_doc_type_ids || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [sub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Latest doc per type for THIS sub
  const latestByType = {};
  docs.forEach((d) => {
    const t = d.doc_type_id;
    if (!latestByType[t] || d.uploaded_at > latestByType[t].uploaded_at) latestByType[t] = d;
  });

  const today = new Date();
  const docStatus = (d) => {
    if (!d.valid_until) return "ok";
    const vu = new Date(d.valid_until);
    if (vu < today) return "expired";
    const diff = (vu - today) / (1000 * 60 * 60 * 24);
    if (diff <= 30) return "expiring_soon";
    return "ok";
  };

  const toggleRequired = async (typeId) => {
    const next = required.includes(typeId) ? required.filter((x) => x !== typeId) : [...required, typeId];
    setRequired(next);
    try {
      await api.updateSubcontractor(sub.id, { required_doc_type_ids: next });
      onUpdated?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
      setRequired(required); // rollback
    }
  };

  const saveToggle = async (key, value) => {
    setSavingToggles(true);
    try {
      await api.updateSubcontractor(sub.id, { [key]: value });
      onUpdated?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de sauvegarde"));
    } finally { setSavingToggles(false); }
  };

  const handleUpload = async (typeId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 10 MB)"); return; }
    setUploading(typeId);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.uploadSubcontractorDocument(sub.id, {
            doc_type_id: typeId,
            file_name: file.name,
            file_base64: reader.result,
            mime_type: file.type,
            valid_until: validityDraft[typeId] || null,
          });
          setValidityDraft((s) => ({ ...s, [typeId]: "" }));
          fetchAll();
          onUpdated?.();
          toast.success("Document ajouté");
        } catch (e) {
          toast.error(getErrorMessage(e, "Erreur d'upload"));
        } finally { setUploading(null); }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      setUploading(null);
      toast.error("Erreur de lecture du fichier");
    }
  };

  const handleViewDocument = async (doc) => {
    try {
      const { data } = await api.getSubcontractorDocumentFile(sub.id, doc.id);
      setViewerDoc(data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur d'ouverture"));
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer "${doc.file_name}" ?`)) return;
    try {
      await api.deleteSubcontractorDocument(sub.id, doc.id);
      fetchAll();
      onUpdated?.();
      toast.success("Document supprimé");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  const handleExportPdf = async () => {
    try {
      toast.message("Génération du PDF en cours…");
      const { data } = await api.exportSubcontractorCompliancePdf(sub.id);
      const link = document.createElement("a");
      link.href = `data:${data.mime_type};base64,${data.file_base64}`;
      link.download = data.file_name || "Conformite.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur génération PDF"));
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground p-4">Chargement…</div>;

  return (
    <div className="space-y-4" data-testid="subcontractor-compliance-tab">
      {/* Export action */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="compliance-export-pdf-btn">
          <FileDown className="h-3.5 w-3.5 mr-1" /> Attestation PDF
        </Button>
      </div>

      {/* Toggles */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">Notifications de conformité</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3 p-3 border rounded-md">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              <div>
                <Label className="text-sm font-medium">Alertes tableau de bord</Label>
                <p className="text-xs text-muted-foreground">Afficher ce sous-traitant dans la section "Alertes Conformité" du dashboard quand un document approche de l'expiration ou manque.</p>
              </div>
            </div>
            <Switch
              checked={alertsEnabled}
              onCheckedChange={(v) => { setAlertsEnabled(v); saveToggle("compliance_alerts_enabled", v); }}
              disabled={savingToggles}
              data-testid="sub-toggle-alerts"
            />
          </div>
          <div className="flex items-center justify-between gap-3 p-3 border rounded-md">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <div>
                <Label className="text-sm font-medium">Email automatique</Label>
                <p className="text-xs text-muted-foreground">
                  Envoyer automatiquement un email au sous-traitant pour lui demander les documents manquants ou expirants.
                  {!sub.email && <span className="text-amber-700"> (Aucune adresse email renseignée — l'envoi sera ignoré.)</span>}
                </p>
              </div>
            </div>
            <Switch
              checked={emailEnabled}
              onCheckedChange={(v) => { setEmailEnabled(v); saveToggle("compliance_auto_email_enabled", v); }}
              disabled={savingToggles || !sub.email}
              data-testid="sub-toggle-auto-email"
            />
          </div>
        </CardContent>
      </Card>

      {/* Required docs + uploads */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">Documents requis</CardTitle>
          <p className="text-xs text-muted-foreground">Cochez les types de documents obligatoires pour ce sous-traitant. Vous pouvez ensuite uploader chaque fichier (PDF ou image, max 10 MB) et indiquer sa date de validité.</p>
        </CardHeader>
        <CardContent>
          {docTypes.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Aucun type de document configuré. Allez dans Paramètres &gt; Configuration pour créer votre bibliothèque (KBIS, Attestation Décennale, etc.).
            </p>
          ) : (
            <div className="space-y-2">
              {docTypes.map((t) => {
                const isRequired = required.includes(t.id);
                const d = latestByType[t.id];
                const status = d ? docStatus(d) : (isRequired ? "incomplete" : "ok");
                const Badge = STATUS_BADGES[status];
                return (
                  <div key={t.id} className="border rounded-md p-3" data-testid={`sub-doctype-${t.id}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Checkbox
                        checked={isRequired}
                        onCheckedChange={() => toggleRequired(t.id)}
                        data-testid={`sub-doctype-required-${t.id}`}
                      />
                      <span className="font-medium text-sm flex-1">{t.name}</span>
                      {isRequired && d && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${Badge.cls}`}>
                          <Badge.Icon className="h-3 w-3" />
                          {Badge.label}
                        </span>
                      )}
                      {isRequired && !d && (
                        <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                          <ShieldAlert className="h-3 w-3" /> Manquant
                        </span>
                      )}
                    </div>
                    {d && (
                      <div className="flex items-center gap-2 mt-2 pl-7 text-xs text-muted-foreground">
                        <FileText className="h-3.5 w-3.5" />
                        <span className="truncate flex-1">{d.file_name}</span>
                        {d.valid_until && <span>Valide jusqu'au <strong className="text-foreground">{formatDate(d.valid_until)}</strong></span>}
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleViewDocument(d)} data-testid={`sub-doc-view-${d.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleDelete(d)} data-testid={`sub-doc-delete-${d.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    )}
                    {isRequired && (
                      <div className="flex items-center gap-2 mt-2 pl-7">
                        <Input
                          type="date"
                          value={validityDraft[t.id] || ""}
                          onChange={(e) => setValidityDraft((s) => ({ ...s, [t.id]: e.target.value }))}
                          className="h-8 text-xs w-40"
                          placeholder="Valide jusqu'au"
                          title="Date de fin de validité (optionnel)"
                          data-testid={`sub-doc-validity-${t.id}`}
                        />
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="application/pdf,image/*"
                            className="hidden"
                            onChange={(e) => handleUpload(t.id, e.target.files?.[0])}
                            data-testid={`sub-doc-upload-${t.id}`}
                          />
                          <Button size="sm" variant="outline" className="h-8" asChild disabled={uploading === t.id}>
                            <span>
                              {uploading === t.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                              {d ? "Remplacer" : "Uploader"}
                            </span>
                          </Button>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Document viewer modal */}
      <Dialog open={!!viewerDoc} onOpenChange={(o) => !o && setViewerDoc(null)}>
        <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="document-viewer">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">{viewerDoc?.file_name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-zinc-100 rounded">
            {viewerDoc && (viewerDoc.mime_type?.startsWith("image/") ? (
              <img
                src={viewerDoc.file_base64.startsWith("data:") ? viewerDoc.file_base64 : `data:${viewerDoc.mime_type};base64,${viewerDoc.file_base64}`}
                alt={viewerDoc.file_name}
                className="w-full h-full object-contain"
              />
            ) : (
              <iframe
                title={viewerDoc.file_name}
                src={viewerDoc.file_base64.startsWith("data:") ? viewerDoc.file_base64 : `data:${viewerDoc.mime_type || "application/pdf"};base64,${viewerDoc.file_base64}`}
                className="w-full h-full"
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
